import React, { useState, useEffect } from 'react';
import { Badge, Offcanvas, ListGroup, Alert, Button } from 'react-bootstrap';
import { API_ENDPOINTS } from '../config/api';

const AnnouncementsCounter = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acknowledgingIds, setAcknowledgingIds] = useState(new Set());
  const [recentlyAcknowledgedIds, setRecentlyAcknowledgedIds] = useState(new Set());
  const [ackMessage, setAckMessage] = useState('');

  useEffect(() => {
    fetchActiveAnnouncements();
    
    // Refresh announcements every 5 minutes to check for expired dismissals
    const interval = setInterval(fetchActiveAnnouncements, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchActiveAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('AnnouncementsCounter: Token check:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenStart: token ? token.substring(0, 20) + '...' : 'none'
      });
      
      if (!token) {
        console.log('AnnouncementsCounter: No token found, skipping fetch');
        return;
      }

      console.log('AnnouncementsCounter: Fetching active announcements...');
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('AnnouncementsCounter: Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('AnnouncementsCounter: Received announcements:', data.length);
        setAnnouncements(data);
      } else {
        const errorText = await response.text();
        console.error('AnnouncementsCounter: Error response:', response.status, errorText);
      }
    } catch (error) {
      console.error('AnnouncementsCounter: Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAnnouncement = async (announcementId) => {
    try {
      console.log('AnnouncementsCounter: Attempting to dismiss announcement:', announcementId);
      console.log('AnnouncementsCounter: Current announcements before dismiss:', announcements);
      
      const token = localStorage.getItem('token');
      console.log('AnnouncementsCounter: Dismiss token check:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenStart: token ? token.substring(0, 20) + '...' : 'none'
      });
      
      if (!token) {
        console.error('AnnouncementsCounter: No token found for dismissal');
        return;
      }

      console.log('AnnouncementsCounter: Sending dismiss request...');
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${announcementId}/dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('AnnouncementsCounter: Dismiss response status:', response.status);
      
      if (response.ok) {
        console.log('AnnouncementsCounter: Announcement dismissed successfully');
        // Remove the dismissed announcement from the list
        setAnnouncements(prev => {
          const dismissId = parseInt(announcementId);
          const filtered = prev.filter(announcement => {
            console.log('AnnouncementsCounter: Comparing announcement ID:', {
              announcementId: announcement.id,
              dismissId: dismissId,
              types: {
                announcementIdType: typeof announcement.id,
                dismissIdType: typeof dismissId
              }
            });
            return announcement.id !== dismissId;
          });
          console.log('AnnouncementsCounter: Announcements after filtering:', filtered);
          return filtered;
        });
      } else {
        const errorData = await response.json();
        console.error('AnnouncementsCounter: Failed to dismiss announcement:', errorData);
        alert(`Failed to dismiss announcement: ${errorData.message}`);
      }
    } catch (error) {
      console.error('AnnouncementsCounter: Error dismissing announcement:', error);
      alert('Error dismissing announcement. Please try again.');
    }
  };

  const acknowledgeAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      setAcknowledgingIds((prev) => new Set(prev).add(announcementId));
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${announcementId}/acknowledge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setRecentlyAcknowledgedIds((prev) => new Set(prev).add(announcementId));
        setAckMessage('Acknowledgement recorded.');
        await fetchActiveAnnouncements();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setAckMessage(errorData.message || 'Failed to acknowledge announcement.');
      }
    } catch (error) {
      console.error('AnnouncementsCounter: Error acknowledging announcement:', error);
      setAckMessage('Failed to acknowledge announcement.');
    } finally {
      setAcknowledgingIds((prev) => {
        const next = new Set(prev);
        next.delete(announcementId);
        return next;
      });
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'urgent':
        return 'danger';
      case 'important':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'urgent':
        return 'bi-exclamation-triangle-fill text-danger';
      case 'important':
        return 'bi-exclamation-circle-fill text-warning';
      case 'info':
        return 'bi-info-circle-fill text-info';
      default:
        return 'bi-megaphone-fill text-primary';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading || announcements.length === 0) {
    return null;
  }

  return (
    <>
      {/* Compact navbar button with counter */}
      <div className="d-flex align-items-center position-relative">
      <Button
        variant="light-lite"
        size="sm"
        className="d-flex align-items-center position-relative"
        title={`${announcements.length} active announcement${announcements.length !== 1 ? 's' : ''}`}
        onClick={() => setShowOffcanvas(true)}
      >
        <i className="bi bi-megaphone-fill"></i>
      </Button>
      
      {announcements.length > 0 && (
        <Badge bg="warning" className="position-absolute top-0 start-100 translate-middle p-1 px-2 rounded-pill" style={{ fontSize: '0.6rem', minWidth: '18px' }}>
          {announcements.length}
        </Badge>
      )}
      </div>

      {/* Announcements Offcanvas */}
      <Offcanvas 
        show={showOffcanvas} 
        onHide={() => setShowOffcanvas(false)}
        placement="end"
        style={{ width: '450px' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex flex-row align-items-center justify-content-center">
            <span className="fw-bold">Active Announcements</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {ackMessage && (
            <Alert
              variant="info"
              className="py-2 mb-3"
              dismissible
              onClose={() => setAckMessage('')}
            >
              {ackMessage}
            </Alert>
          )}
          <ListGroup variant="flush">
            {announcements.map((announcement) => (
              <ListGroup.Item key={announcement.id} className="border-0 mb-3">
                <div className="d-flex align-items-start gap-2">
                  <div className="flex-shrink-0">
                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, fontSize: 14 }}>
                      <i className={`bi ${getTypeIcon(announcement.type)}`} style={{ fontSize: '1.2rem' }} />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h6 className="mb-0">{announcement.title}</h6>
                      <Badge bg={getTypeColor(announcement.type)} size="sm">
                        {announcement.type}
                      </Badge>
                      {announcement.requiresAcknowledgement && (
                        <Badge bg="danger" size="sm">Ack required</Badge>
                      )}
                      {(!announcement.requiresAcknowledgement || announcement.hasAcknowledged || recentlyAcknowledgedIds.has(announcement.id)) && (
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => dismissAnnouncement(announcement.id)}
                          title={announcement.requiresAcknowledgement ? 'Dismiss after acknowledgement' : 'Dismiss for 24 hours'}
                          className="ms-auto p-1 px-2"
                        >
                          <i className="bi bi-x-lg" />
                        </Button>
                      )}
                    </div>
                    <p className="text-muted small mb-2">
                      {announcement.content}
                    </p>
                    {Array.isArray(announcement.attachments) && announcement.attachments.length > 0 && (
                      <div className="mb-2">
                        {announcement.attachments.map((link, idx) => (
                          <div key={`${announcement.id}-att-${idx}`}>
                            <a href={link} target="_blank" rel="noopener noreferrer" className="small">
                              Attachment {idx + 1}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    {announcement.requiresAcknowledgement && !(announcement.hasAcknowledged || recentlyAcknowledgedIds.has(announcement.id)) && (
                      <div className="d-flex justify-content-end mb-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={acknowledgingIds.has(announcement.id)}
                          onClick={() => acknowledgeAnnouncement(announcement.id)}
                        >
                          {acknowledgingIds.has(announcement.id) ? 'Saving...' : 'I acknowledge'}
                        </Button>
                      </div>
                    )}
                    {announcement.requiresAcknowledgement && (announcement.hasAcknowledged || recentlyAcknowledgedIds.has(announcement.id)) && (
                      <div className="d-flex justify-content-end mb-2">
                        <Badge bg="success" className="px-2 py-1">Acknowledged</Badge>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {formatDate(announcement.startDate)} - {formatDate(announcement.endDate)}
                      </small>
                      <small className="text-muted">
                        by {announcement.creator?.firstName} {announcement.creator?.lastName}
                      </small>
                    </div>

                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AnnouncementsCounter; 